import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, theme as antdTheme, Switch, ConfigProvider } from 'antd';
import {
  UserOutlined,
  FileTextOutlined,
  DashboardOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  KeyOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  UserSwitchOutlined,
  LockOutlined,
  BulbOutlined,
  MoonOutlined,
  DeploymentUnitOutlined,
  RobotOutlined,
  ApiOutlined,
  WalletOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { usePermissionCheck, usePermissions, PermissionGuard } from '../../context/PermissionContext';
import { authApi } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';
import { requestDeduplication } from '../../utils/requestDeduplication';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const AdminLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState<{ name: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = antdTheme.useToken();
  const { theme, toggleTheme } = useTheme();

  const { clearPermissions } = usePermissions();
  const {
    canViewDashboard,
    canViewUsers,
    canViewAPIs,
    canViewLogs,
    canViewUserLogs,
    canViewAPILogs,
    canViewStats,
    canViewPermissions,
    canViewRoles,
    canViewUserRoles,
    canAssignRoles,
    canViewModels,
    canViewPreferences,
    canViewApiTest,
    canViewRecharge,
    canViewRechargeOrders,
    canViewChat,
  } = usePermissionCheck();

  // 获取当前用户信息
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        // 使用请求去重，防止重复调用
        const response = await requestDeduplication.request(
          'getCurrentUser',
          () => authApi.getCurrentUser()
        );
        setCurrentUser(response.data);
        console.log('✅ 用户信息加载完成:', response.data.name);
      } catch (error) {
        console.error('获取当前用户信息失败:', error);
        // 如果获取失败，可能是token过期，跳转到登录页
        localStorage.removeItem('admin_token');
        navigate('/login');
      }
    };

    const token = localStorage.getItem('admin_token');
    if (token && !currentUser) {
      fetchCurrentUser();
    } else if (!token) {
      navigate('/login');
    }
  }, [navigate, currentUser]); // 添加currentUser依赖项

  // 构建动态菜单项配置
  const buildMenuItems = (): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    // 仪表盘
    if (canViewDashboard()) {
      items.push({
        key: '/admin/dashboard',
        icon: <DashboardOutlined />,
        label: '仪表盘',
      });
    }

    // 用户管理
    if (canViewUsers()) {
      items.push({
        key: '/admin/users',
        icon: <UserOutlined />,
        label: '用户管理',
      });
    }

    // API密钥管理
    if (canViewAPIs()) {
      items.push({
        key: '/admin/apikeys',
        icon: <KeyOutlined />,
        label: 'API密钥管理',
      });
    }

    // 大模型管理
    if (canViewModels()) {
      items.push({
        key: '/admin/models',
        icon: <DeploymentUnitOutlined />,
        label: '大模型管理',
      });
    }

    // 充值管理
    if (canViewRecharge && canViewRecharge()) {
      items.push({
        key: '/admin/recharge',
        icon: <WalletOutlined />,
        label: '充值管理',
      });
    }

    // 充值订单管理
    if (canViewRechargeOrders && canViewRechargeOrders()) {
      items.push({
        key: '/admin/recharge-orders',
        icon: <WalletOutlined />,
        label: '充值订单管理',
      });
    }

    // 使用信息
    if (canViewPreferences()) {
      items.push({
        key: '/admin/usage-info',
        icon: <SettingOutlined />,
        label: '使用信息',
      });
    }

    // API测试工具
    if (canViewApiTest()) {
      items.push({
        key: '/admin/api-test',
        icon: <ApiOutlined />,
        label: 'API测试',
      });
    }

    // AI聊天
    if (canViewChat()) {
      items.push({
        key: '/admin/chat',
        icon: <MessageOutlined />,
        label: 'AI聊天',
      });
    }

    // 权限管理子菜单
    const permissionMenuChildren: MenuProps['items'] = [];
    
    if (canViewPermissions()) {
      permissionMenuChildren.push({
        key: '/admin/permissions',
        icon: <LockOutlined />,
        label: '权限管理',
      });
    }

    if (canViewRoles()) {
      permissionMenuChildren.push({
        key: '/admin/roles',
        icon: <TeamOutlined />,
        label: '角色管理',
      });
    }

    if (canViewUserRoles()) {
      permissionMenuChildren.push({
        key: '/admin/user-roles',
        icon: <UserSwitchOutlined />,
        label: '用户角色',
      });
    }

    // 只有有权限访问至少一个子菜单时才显示权限管理菜单
    if (permissionMenuChildren.length > 0) {
      items.push({
        key: 'permission-management',
        icon: <SafetyCertificateOutlined />,
        label: '权限管理',
        children: permissionMenuChildren,
      });
    }

    // 日志管理
    const logMenuChildren: MenuProps['items'] = [];
    
    if (canViewUserLogs()) {
      logMenuChildren.push({
        key: '/admin/logs',
        label: '用户日志',
      });
    }

    if (canViewAPILogs()) {
      logMenuChildren.push({
        key: '/admin/api-logs',
        label: 'API调用日志',
      });
      logMenuChildren.push({
        key: '/admin/billing-records',
        label: '消费记录',
      });
    }

    if (canViewStats()) {
      logMenuChildren.push({
        key: '/admin/stats',
        label: '统计信息',
      });
    }

    // 只有有权限访问至少一个子菜单时才显示日志管理菜单
    if (logMenuChildren.length > 0) {
      items.push({
        key: 'log-management',
        icon: <FileTextOutlined />,
        label: '日志管理',
        children: logMenuChildren,
      });
    }

    return items;
  };

  // 用户下拉菜单
  const userMenuItems: MenuProps['items'] = [
    {
      key: '/admin/settings',
      icon: <SettingOutlined />,
      label: '修改密码',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      console.log('🚪 用户退出登录');
      // 清除token和用户信息
      localStorage.removeItem('admin_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_info');
      // 清空权限信息
      clearPermissions();
      // 跳转到登录页
      navigate('/login');
    } else if (key === '/admin/settings') {
      navigate('/admin/settings');
    }
  };

  // 获取当前选中的菜单key
  const getSelectedKeys = () => {
    const pathname = location.pathname;
    // 处理嵌套菜单的选中状态
    if (pathname === '/admin/permissions' || pathname === '/admin/roles' || pathname === '/admin/user-roles') {
      return [pathname];
    }
    // 处理大模型管理页面
    if (pathname === '/admin/models') {
      return [pathname];
    }
    // 处理用户偏好和API测试页面
    if (pathname === '/admin/usage-info' || pathname === '/admin/api-test') {
      return [pathname];
    }
    // 处理聊天页面
    if (pathname === '/admin/chat') {
      return [pathname];
    }
    return [pathname];
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      }}
    >
      <Layout style={{ minHeight: '100vh' }}>
        <Sider trigger={null} collapsible collapsed={collapsed}>
          <div style={{ 
            height: 32, 
            margin: 16, 
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold'
          }}>
            {collapsed ? 'AL' : 'APILink'}
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={getSelectedKeys()}
            items={buildMenuItems()}
            onClick={handleMenuClick}
          />
        </Sider>
        <Layout>
          <Header style={{ 
            padding: '0 16px', 
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                className: 'trigger',
                onClick: () => setCollapsed(!collapsed),
                style: { fontSize: '18px', cursor: 'pointer' }
              })}
              <Title level={4} style={{ margin: '0 0 0 16px' }}>
                管理后台
              </Title>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={theme === 'dark'}
                onChange={toggleTheme}
                checkedChildren={<MoonOutlined />}
                unCheckedChildren={<BulbOutlined />}
                style={{ marginRight: 16 }}
              />
              <Dropdown 
                menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                placement="bottomRight"
              >
                <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <Avatar icon={<UserOutlined />} />
                  <span style={{ marginLeft: 8 }}>{currentUser?.name || '管理员'}</span>
                </div>
              </Dropdown>
            </div>
          </Header>
          <Content
            style={{
              margin: '24px 16px',
              padding: 24,
              minHeight: 280,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

export default AdminLayout; 